import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadBulkImportFile, listBulkImports } from '../api/bulk';
import type { BulkImport } from '../api/types';
import { saveBlob } from '../download';
import { formatTimestamp } from '../format';

function authorOf(item: BulkImport): string {
  return item.appliedByName ?? item.appliedByEmail ?? item.appliedBy;
}

export function BulkImportHistory({
  groupId,
  refreshKey,
  onError,
}: {
  groupId: string;
  refreshKey: number;
  onError: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const [imports, setImports] = useState<BulkImport[]>([]);
  const [mine, setMine] = useState(false);

  const reload = useCallback(async () => {
    try {
      setImports(await listBulkImports(groupId, mine));
    } catch (caught) {
      onError(caught);
    }
  }, [groupId, mine, onError]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  async function downloadFile(item: BulkImport) {
    try {
      saveBlob(await downloadBulkImportFile(item.id), item.fileName);
    } catch (caught) {
      onError(caught);
    }
  }

  return (
    <div className="card">
      <div className="card-heading">
        <h2>
          <i className="pi pi-history me-2" aria-hidden="true" />
          {t('bulk.history.title')}
        </h2>
        <div className="form-check form-switch ms-auto mb-0">
          <input
            id="bulk-history-mine"
            type="checkbox"
            role="switch"
            className="form-check-input"
            checked={mine}
            onChange={(event) => setMine(event.target.checked)}
          />
          <label className="form-check-label" htmlFor="bulk-history-mine">
            {t('bulk.history.onlyMine')}
          </label>
        </div>
      </div>

      {imports.length === 0 ? (
        <p className="hint">{t('bulk.history.none')}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm bulk-table align-middle" aria-label={t('bulk.history.title')}>
            <thead>
              <tr>
                <th scope="col">{t('bulk.history.when')}</th>
                <th scope="col">{t('bulk.history.who')}</th>
                <th scope="col">{t('bulk.history.file')}</th>
                <th scope="col">{t('bulk.history.what')}</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((item) => (
                <tr key={item.id}>
                  <td className="text-nowrap">{formatTimestamp(item.appliedAt)}</td>
                  <td>
                    {authorOf(item)}
                    {item.appliedByMe && <span className="badge badge-neutral ms-1">{t('common.you')}</span>}
                  </td>
                  <td>
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void downloadFile(item)}>
                      <i className="pi pi-file me-1" aria-hidden="true" />
                      {item.fileName}
                    </button>
                  </td>
                  <td>
                    {t('bulk.history.counts', {
                      created: item.createdCount,
                      updated: item.updatedCount,
                      unchanged: item.unchangedCount,
                      skipped: item.skippedCount,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
